import React, { Ref, useCallback, useMemo, useState } from 'react';
import useSelectInnerText from 'src/hooks/useSelectInnerText';
import { useMergeRefs } from '@floating-ui/react';
import {
	format as reactNumberFormat,
	unformat as reactNumberUnformat,
	useNumberFormat,
} from '@react-input/number-format';
import { BaseNumberInputProps } from 'src/components/primitives/Input/types';
import { getAutoCompleteProps } from 'src/components/primitives/Input/utils';
import { roundToStepSizeIfLargeEnough } from '@velocity-exchange/common';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { getIsIos } from 'src/utils/browser';

type Params = Pick<
	BaseNumberInputProps,
	| 'value'
	| 'onChange'
	| 'onEnter'
	| 'onEscape'
	| 'onKeyUp'
	| 'onClick'
	| 'onBlur'
	| 'onFocus'
	| 'validate'
	| 'autoComplete'
	| 'placeholder'
	| 'allowNegative'
	| 'enableSelectAllOnFocus'
	| 'numberFormatProps'
	| 'stepSize'
	| 'ref'
>;

interface UseNumberInput {
	value: string;
	/**
	 * Hardcoded to text, as this gives us full control over formatting.
	 */
	type: 'text';
	/**
	 * Defaults to `decimal`.
	 *
	 * If `allowNegative` is `true` and we're on iOS, uses `text`,
	 * as the iOS "decimal" keypad doesn't have a "-" key.
	 */
	inputMode: 'decimal' | 'text';
	/**
	 * Defaults to `0.00`.
	 */
	placeholder: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onKeyUp: (e: React.KeyboardEvent<HTMLInputElement>) => void;
	onClick: (e: React.MouseEvent<HTMLInputElement>) => void;
	onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
	onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
	ref: Ref<HTMLInputElement>;
}

/**
 * Returns props for a formatted number input.
 */
export default function useNumberInput({
	value: valueFromProps,
	onChange: onChangeFromProps,
	onClick: onClickFromProps,
	onFocus: onFocusFromProps,
	onBlur: onBlurFromProps,
	onKeyUp: onKeyUpFromProps,
	onEnter,
	onEscape,
	allowNegative,
	placeholder,
	numberFormatProps,
	enableSelectAllOnFocus,
	autoComplete,
	stepSize,
	validate,
	ref,
}: Params) {
	const [selectInnerText, resetSelectInnerText] = useSelectInnerText();
	const [isFocused, setIsFocused] = React.useState(false);

	// Defines the options to use for all formatting.
	const numberFormatParams = useMemo((): Parameters<
		typeof useNumberFormat
	>[0] => {
		return {
			locales: Env.locale,
			signDisplay: allowNegative ? 'auto' : 'never',
			maximumIntegerDigits: 12,
			maximumFractionDigits: 10,
			...numberFormatProps,
		};
	}, [numberFormatProps, allowNegative]);

	// Simple helper wrapper around the library's format function, passing the options.
	const format = useCallback(
		(value: string) => {
			return reactNumberFormat(value, numberFormatParams);
		},
		[numberFormatParams]
	);

	// Simple helper wrapper around the library's unformat function, passing the locale.
	const unformat = useCallback((value: string) => {
		return reactNumberUnformat(value, Env.locale);
	}, []);

	// Internal state value of the input, not always the value displayed (see `displayedValue`).
	const [userInput, setUserInput] = useState<string>();

	// When the input is focused, we show whatever the user is typing. Else,
	// show the externally bound value. We don't want to show the external
	// value when the input is focused as this would overwrite what the user
	// is typing, which is generally discouraged by UX guidelines.
	const displayedValue = useMemo(() => {
		if (isFocused && userInput !== undefined) {
			return userInput;
		}

		return valueFromProps ? format(valueFromProps) : '';
	}, [isFocused, userInput, valueFromProps, format]);

	const inputRef = useNumberFormat(numberFormatParams);
	const mergedRef = useMergeRefs([ref, inputRef]);

	const onChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const newValue = e.target.value;
			const unformatted = unformat(newValue);

			// For some reason the library formats `-` to `-0`, so in this case
			// we cut off the `0` for the user input and skip updating externally.
			if (newValue === '-0') {
				setUserInput('-');
				return;
			}

			const valueToValidate = (() => {
				// If new value is something like `32.`, `unformatted` will only be `32`.
				// So we need to append the `.` to `unformatted` for validation targeting
				// integers / decimals.
				if (newValue.endsWith('.')) {
					return unformatted + '.';
				}

				return unformatted;
			})();

			// Skip any updates if the caller's eager validation fails.
			if (validate && !validate(valueToValidate)) {
				return;
			}

			// Only update the external value if what we have is a valid number.
			// If we don't do this, stuff will break when trying to parse the value.
			// Only exception is the empty string, which is safe to update externally.
			if (!isNaN(parseFloat(unformatted)) || unformatted === '') {
				// Round to nearest step size if large enough.
				const valueToPass = stepSize
					? roundToStepSizeIfLargeEnough(unformatted, stepSize)
					: unformatted;

				onChangeFromProps?.(valueToPass);
			}

			setUserInput(newValue);
		},
		[onChangeFromProps, unformat, stepSize, validate]
	);

	const onKeyUp = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			onKeyUpFromProps?.(event);

			if (event.key === 'Enter') {
				// Blur the input so the externally updated value can be used if necessary.
				inputRef.current?.blur();

				onEnter?.();
				return;
			}

			if (event.key === 'Escape') {
				// Blur the input so the externally updated value can be used if necessary.
				inputRef.current?.blur();

				onEscape?.();
				return;
			}
		},
		[inputRef, onEnter, onEscape, onKeyUpFromProps]
	);

	const onClick = useCallback(
		(e: React.MouseEvent<HTMLInputElement>) => {
			if (enableSelectAllOnFocus) {
				selectInnerText(e);
			}

			onClickFromProps?.(e);
		},
		[enableSelectAllOnFocus, onClickFromProps, selectInnerText]
	);

	const onFocus = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			setIsFocused(true);

			// On focus, begin showing the user input.
			setUserInput(e.target.value);

			onFocusFromProps?.(e);
		},
		[onFocusFromProps]
	);

	const onBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			setIsFocused(false);

			// Clear the user input on blur, so we begin showing the external value.
			setUserInput(undefined);

			if (enableSelectAllOnFocus) {
				resetSelectInnerText();
			}

			onBlurFromProps?.(e);
		},
		[enableSelectAllOnFocus, onBlurFromProps, resetSelectInnerText]
	);

	return useMemo((): UseNumberInput => {
		return {
			value: displayedValue,
			type: 'text',
			inputMode: allowNegative && getIsIos() ? 'text' : 'decimal',
			placeholder: placeholder ?? '0.00',
			onChange,
			onKeyUp,
			onClick,
			onFocus,
			onBlur,
			ref: mergedRef,
			...getAutoCompleteProps(autoComplete),
		};
	}, [
		displayedValue,
		allowNegative,
		placeholder,
		onChange,
		onKeyUp,
		onClick,
		onFocus,
		onBlur,
		mergedRef,
		autoComplete,
	]);
}
